/**
 * Token Storage Service
 *
 * Stores refresh tokens in IndexedDB with encryption for PWA persistence.
 * Access tokens are kept in memory only for security.
 *
 * Security features:
 * - Refresh tokens encrypted with AES-GCM before storage
 * - Encryption key derived from device-specific factors
 * - Tokens never stored in localStorage (vulnerable to XSS)
 */

const DB_NAME = 'player-central-auth';
const DB_VERSION = 1;
const STORE_NAME = 'tokens';

interface TokenRecord {
  id: string;
  encryptedToken: ArrayBuffer;
  iv: Uint8Array;
  createdAt: number;
}

class TokenStorageService {
  private db: IDBDatabase | null = null;
  private encryptionKey: CryptoKey | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the storage service
   */
  async initialize(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._initialize();
    return this.initPromise;
  }

  private async _initialize(): Promise<void> {
    // Check if IndexedDB is available
    if (!window.indexedDB) {
      console.warn('IndexedDB not available, falling back to localStorage');
      return;
    }

    // Check if crypto.subtle is available (requires HTTPS or localhost)
    if (!window.crypto?.subtle) {
      console.warn('crypto.subtle not available (requires HTTPS), falling back to localStorage');
      return;
    }

    try {
      // Open IndexedDB
      this.db = await this.openDatabase();

      // Derive encryption key
      await this.deriveEncryptionKey();
    } catch (error) {
      console.error('Failed to initialize token storage:', error);
      // Don't throw - gracefully degrade to localStorage fallback
    }
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  }

  private async deriveEncryptionKey(): Promise<void> {
    // Use a combination of factors as salt
    // This provides some binding to the device/browser
    const encoder = new TextEncoder();
    const salt = encoder.encode(
      `${navigator.userAgent}-${window.location.origin}-player-central`
    );

    // Import a fixed base key
    const baseKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode('pwa-token-encryption-base-v1'),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    // Derive the actual encryption key
    this.encryptionKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Store a refresh token securely
   */
  async storeRefreshToken(token: string): Promise<void> {
    // Fallback to localStorage if IndexedDB/encryption not available
    if (!this.db || !this.encryptionKey) {
      localStorage.setItem('pc_rt', token);
      return;
    }

    try {
      const encoder = new TextEncoder();
      const iv = crypto.getRandomValues(new Uint8Array(12));

      const encryptedToken = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        this.encryptionKey,
        encoder.encode(token)
      );

      const record: TokenRecord = {
        id: 'refresh-token',
        encryptedToken,
        iv,
        createdAt: Date.now(),
      };

      await this.putRecord(record);

      // Also keep localStorage as backup in case IndexedDB becomes unavailable
      localStorage.setItem('pc_rt', token);
    } catch (error) {
      console.error('Failed to store refresh token in IndexedDB:', error);
      // Fallback to localStorage
      localStorage.setItem('pc_rt', token);
    }
  }

  /**
   * Retrieve the refresh token
   */
  async getRefreshToken(): Promise<string | null> {
    // Check localStorage fallback first
    const fallbackToken = localStorage.getItem('pc_rt');

    if (!this.db || !this.encryptionKey) {
      return fallbackToken;
    }

    try {
      const record = await this.getRecord('refresh-token');

      if (!record) {
        return fallbackToken;
      }

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: new Uint8Array(record.iv) },
        this.encryptionKey,
        record.encryptedToken
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('Failed to retrieve refresh token from IndexedDB:', error);
      // Decryption failed - could be key change, corruption, or transient error
      // Only clear the IndexedDB record, preserve localStorage fallback if it exists
      try {
        await this.deleteRecord('refresh-token');
      } catch {
        // Ignore cleanup errors
      }
      // Return localStorage fallback if available
      return fallbackToken;
    }
  }

  /**
   * Clear all stored tokens
   */
  async clearTokens(): Promise<void> {
    localStorage.removeItem('pc_rt');

    if (!this.db) {
      return;
    }

    try {
      await this.deleteRecord('refresh-token');
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  /**
   * Check if a refresh token exists
   */
  async hasRefreshToken(): Promise<boolean> {
    const token = await this.getRefreshToken();
    return token !== null;
  }

  // IndexedDB helpers
  private putRecord(record: TokenRecord): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(record);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  private getRecord(id: string): Promise<TokenRecord | null> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
    });
  }

  private deleteRecord(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}

export const tokenStorage = new TokenStorageService();
